import { useCallback, useMemo, useRef, useState } from 'react';
import { useAppToken } from './useAppToken';
import { useWorkspace } from '../context/WorkspaceContext';
import { usePreferences } from '../context/PreferencesContext';
import { useToast } from '../components/workspace/Toast';

const API_URL = process.env.REACT_APP_API_URL || '';

const SECTION_ORDER = [
  'job_title', 'about_company', 'job_description',
  'requirements', 'benefits', 'conditions',
];

const SECTION_LABELS_INTERNAL = {
  job_title: 'Vacaturetitel',
  about_company: 'Over het bedrijf',
  job_description: 'Functieomschrijving',
  requirements: 'Functie-eisen',
  benefits: 'Wat wij bieden',
  conditions: 'Arbeidsvoorwaarden',
};

const REGEN_INSTRUCTIONS = {
  rewrite: 'Herschrijf deze sectie volledig. Maak een nieuwe, frisse versie met dezelfde kerninformatie maar andere formulering, zinsbouw en woordkeuze. Behoud lengte en stijl van de rest van het document.',
  shorter: 'Maak deze sectie korter en bondiger, halveer de lengte. Behoud uitsluitend de belangrijkste informatie. Verwijder herhalingen, vulwoorden en niet-essentiële details. Behoud de toon van de rest van het document.',
};

const REGEN_CHAT_LABELS = {
  rewrite: '🔄 Herschrijven',
  shorter: '✂️ Inkorten',
};

/** Counter for generating unique card IDs within a session */
let cardIdCounter = 0;

/**
 * Normalize card data from backend API format to the shape that
 * DynamicCard / ChoiceCard / ConfirmCard / SuggestionCard expect.
 *
 * Backend returns options as a string array: ["ICT", "Zorg", ...]
 * ChoiceCard expects objects: [{ id: "ict", label: "ICT" }, ...]
 */
function normalizeCardData(msg) {
  const id = `card-${++cardIdCounter}`;

  if (msg.type === 'choice') {
    const options = (msg.options || []).map((opt, i) => {
      if (typeof opt === 'string') {
        return { id: `${id}-opt-${i}`, label: opt };
      }
      // Already an object, ensure it has an id
      return { id: opt.id || `${id}-opt-${i}`, label: opt.label || opt.text || String(opt), ...opt };
    });
    return { ...msg, id, options };
  }

  if (msg.type === 'confirm') {
    return {
      ...msg,
      id,
      fields: msg.fields || (msg.content ? [{ label: '', value: msg.content }] : []),
    };
  }

  if (msg.type === 'suggestion') {
    return {
      ...msg,
      id,
      // Backend sends "content", SuggestionCard expects "suggestion"
      suggestion: msg.suggestion || msg.content || '',
    };
  }

  return { ...msg, id };
}

/**
 * useStructuredChat. Communicates with backend structured JSON endpoint
 *
 * Returns:
 *  - sendMessage(text)              Send user text to backend
 *  - sendCardResponse(cardId, response)  Send card interaction to backend
 *  - isLoading                      Request in progress
 */
export function useStructuredChat() {
  const { getToken } = useAppToken();
  const { state, dispatch, addMessage, updateSection } = useWorkspace();
  const { buildPromptContext, persona } = usePreferences();
  const [isLoading, setIsLoading] = useState(false);
  const showToast = useToast();
  const hasShownFirstConceptToastRef = useRef(false);

  // Keep buildPromptContext in a ref so callStructured's stable callback can
  // read the latest prefs without re-creating itself (would invalidate downstream
  // memoization in sendMessage / regenerateSection / etc).
  const buildPromptContextRef = useRef(buildPromptContext);
  buildPromptContextRef.current = buildPromptContext;
  const personaNameRef = useRef(persona?.name || 'Matcher');
  personaNameRef.current = persona?.name || 'Matcher';

  // Ref keeps the latest sessionId in sync, avoids stale closure issues
  // when callStructured is invoked right after a dispatch that sets the sessionId.
  const sessionIdRef = useRef(state.session.sessionId);
  sessionIdRef.current = state.session.sessionId;

  const callStructured = useCallback(async (body) => {
    setIsLoading(true);
    dispatch({ type: 'SET_PROCESSING', payload: true });

    try {
      // AIVD demo: token optional. getToken() may throw if Authentik env
      // missing; fall back to anonymous request.
      let token = null;
      try { token = await getToken(); } catch { /* no auth in demo */ }

      const userPreferences = buildPromptContextRef.current?.();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch(`${API_URL}/api/chat/structured`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          sessionId: body.sessionId || sessionIdRef.current,
          ...(userPreferences ? { userPreferences } : {}),
          personaName: personaNameRef.current,
          ...body,
        }),
      });

      if (!res.ok) throw new Error(`API error: ${res.status}`);

      const data = await res.json();

      // Process messages
      if (data.messages) {
        for (const msg of data.messages) {
          if (msg.type === 'text') {
            addMessage('ai', msg.content);
          } else if (['choice', 'confirm', 'suggestion'].includes(msg.type)) {
            const normalized = normalizeCardData(msg);
            addMessage('ai', msg.title || '', normalized);
          }
        }
      }

      // Process document updates
      if (data.documentUpdates) {
        for (const update of data.documentUpdates) {
          updateSection(update.section, update.content, update.status || 'draft');
        }
        // Concept-generation moment: batch of 4+ sections means the AI is
        // generating the full concept (Fase 2 in chat.js prompt). Unlocks the
        // completion-popup gate even if prompt-drift skips the choice-card flow.
        if (data.documentUpdates.length >= 4) {
          dispatch({ type: 'SET_CONCEPT_CONFIRMED' });
        }
      }

      // "Eerste concept klaar"-toast fires only on the actual concept-batch moment
      // (4+ sections filled in one turn), same threshold as the userConfirmedConcept
      // safety net. Earlier this fired on ANY documentUpdates length > 0, which was
      // misleading when just 1 section got filled from a URL-submission.
      if (data.documentUpdates?.length >= 4 && !hasShownFirstConceptToastRef.current) {
        hasShownFirstConceptToastRef.current = true;
        showToast('✨ Eerste concept klaar, verfijn nu samen met de AI', 'success');
      }

      // Update title if provided
      if (data.documentTitle) {
        dispatch({ type: 'SET_DOCUMENT_TITLE', payload: data.documentTitle });
      }

      return data;
    } catch (err) {
      console.error('[StructuredChat] Error:', err);
      addMessage('system', 'Er ging iets mis. Probeer het opnieuw.');
      return null;
    } finally {
      setIsLoading(false);
      dispatch({ type: 'SET_PROCESSING', payload: false });
    }
  }, [getToken, dispatch, addMessage, updateSection]);

  /**
   * Silent sync, sends voice transcript to backend for document extraction only.
   * Does NOT add messages to chat or show typing indicator (voice handles that).
   */
  const syncVoiceProgress = useCallback(async (voiceMessages) => {
    if (!voiceMessages.length) return null;

    // Map UI shape → backend shape: {sender: user|ai} → {role: user|assistant}
    const payloadMessages = voiceMessages
      .filter((m) => m && typeof m.content === 'string' && m.content.trim())
      .map((m) => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.content,
      }));

    try {
      const token = await getToken();
      const res = await fetch(`${API_URL}/api/chat/voice-sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sessionId: sessionIdRef.current,
          voiceMessages: payloadMessages,
        }),
      });

      if (!res.ok) return null;
      const data = await res.json();

      if (data.documentUpdates) {
        for (const update of data.documentUpdates) {
          updateSection(update.section, update.content, update.status || 'draft');
        }
        if (data.documentUpdates.length >= 4) {
          dispatch({ type: 'SET_CONCEPT_CONFIRMED' });
        }
      }
      if (data.documentTitle) {
        dispatch({ type: 'SET_DOCUMENT_TITLE', payload: data.documentTitle });
      }

      return data;
    } catch (err) {
      console.error('[StructuredChat] Silent sync error:', err);
      return null;
    }
  }, [getToken, dispatch, updateSection]);

  /**
   * Initialize session, shows hardcoded welcome message instantly (no API call).
   * The backend seeds this message in session history for context continuity.
   */
  const initSession = useCallback(() => {
    hasShownFirstConceptToastRef.current = false;
    // KEEP IN SYNC with backend/routes/chat.js WELCOME_TEXT + TOPIC_CHOICE_CARD.
    addMessage('ai', 'Welkom bij de Humanaize Matcher. Vertel me waar je iemand voor zoekt, neem de tijd. Ik luister mee en zoek ondertussen naar de best passende student-engineers uit ons team. Klik op de microfoon om te praten, typ, of kies hieronder waar je mee wil starten.');

    const topicCard = normalizeCardData({
      type: 'choice',
      title: 'Waar wil je mee beginnen?',
      options: [
        { label: 'De opdracht', icon: 'assignment' },
        { label: 'Het soort persoon', icon: 'person_search' },
        { label: 'Het tijdspad', icon: 'schedule' },
      ],
    });
    addMessage('ai', topicCard.title, topicCard);
  }, [addMessage]);

  /**
   * Upload a file, sends it to the backend for text extraction and AI processing.
   */
  const uploadFile = useCallback(async (file) => {
    setIsLoading(true);
    dispatch({ type: 'SET_PROCESSING', payload: true });
    addMessage('system', `Bestand "${file.name}" wordt verwerkt...`);

    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('sessionId', sessionIdRef.current);

      const res = await fetch(`${API_URL}/api/chat/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      const data = await res.json();

      if (data.messages) {
        for (const msg of data.messages) {
          if (msg.type === 'text') {
            addMessage('ai', msg.content);
          } else if (['choice', 'confirm', 'suggestion'].includes(msg.type)) {
            const normalized = normalizeCardData(msg);
            addMessage('ai', msg.title || '', normalized);
          }
        }
      }
      if (data.documentUpdates) {
        for (const update of data.documentUpdates) {
          updateSection(update.section, update.content, update.status || 'draft');
        }
        if (data.documentUpdates.length >= 4) {
          dispatch({ type: 'SET_CONCEPT_CONFIRMED' });
        }
      }
      if (data.documentUpdates?.length >= 4 && !hasShownFirstConceptToastRef.current) {
        hasShownFirstConceptToastRef.current = true;
        showToast('✨ Eerste concept klaar, verfijn nu samen met de AI', 'success');
      }
      if (data.documentTitle) {
        dispatch({ type: 'SET_DOCUMENT_TITLE', payload: data.documentTitle });
      }
      return data;
    } catch (err) {
      console.error('[StructuredChat] Upload error:', err);
      addMessage('system', 'Upload mislukt. Probeer het opnieuw.');
      return null;
    } finally {
      setIsLoading(false);
      dispatch({ type: 'SET_PROCESSING', payload: false });
    }
  }, [getToken, dispatch, addMessage, updateSection]);

  const sendMessage = useCallback((text) => {
    // Show user message in conversation immediately
    addMessage('user', text);
    return callStructured({ message: text });
  }, [callStructured, addMessage]);

  /**
   * Process voice transcript, sends voice conversation history to the backend
   * so it can (a) extract document updates and (b) seed session history for
   * context continuity when the user switches to text.
   */
  /**
   * Process voice transcript silently, extracts document updates from the
   * voice conversation without adding duplicate messages to the chat.
   * The voice agent already handled the conversation; we only need the structured data.
   */
  const processVoiceTranscript = useCallback(async (voiceMessages) => {
    if (!voiceMessages.length) return null;

    const payloadMessages = voiceMessages
      .filter((m) => m && typeof m.content === 'string' && m.content.trim())
      .map((m) => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.content,
      }));

    try {
      const token = await getToken();
      const res = await fetch(`${API_URL}/api/chat/voice-sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sessionId: sessionIdRef.current,
          voiceMessages: payloadMessages,
        }),
      });

      if (!res.ok) return null;
      const data = await res.json();

      if (data.documentUpdates) {
        for (const update of data.documentUpdates) {
          updateSection(update.section, update.content, update.status || 'draft');
        }
      }
      if (data.documentTitle) {
        dispatch({ type: 'SET_DOCUMENT_TITLE', payload: data.documentTitle });
      }

      return data;
    } catch (err) {
      console.error('[StructuredChat] Voice transcript error:', err);
      return null;
    }
  }, [getToken, dispatch, updateSection]);

  const resetFirstConceptToast = useCallback(() => {
    hasShownFirstConceptToastRef.current = false;
  }, []);

  const sendCardResponse = useCallback((cardId, response) => {
    dispatch({ type: 'HANDLE_CARD_RESPONSE', payload: { cardId, response } });
    // Send readable response text + structured cardResponse matching backend format
    return callStructured({
      message: response,
      cardResponse: { type: 'choice', value: response },
    });
  }, [callStructured, dispatch]);

  const regenerateSection = useCallback((sectionKey, action) => {
    const doc = state.document;
    const sectionContent = doc.sections[sectionKey]?.content || '';
    if (!sectionContent.trim()) return;

    const allSections = SECTION_ORDER
      .filter(k => k !== sectionKey && doc.sections[k]?.content?.trim())
      .map(k => `[${SECTION_LABELS_INTERNAL[k]}]\n${doc.sections[k].content}`)
      .join('\n\n');

    const instruction = REGEN_INSTRUCTIONS[action] || REGEN_INSTRUCTIONS.rewrite;

    const prompt =
      `CONTEXT. Dit is de volledige vacaturetekst tot nu toe:\n` +
      `Functietitel: ${doc.title || '(onbekend)'}\n\n` +
      `${allSections}\n\n` +
      `---\n` +
      `OPDRACHT: Pas ALLEEN de sectie "${SECTION_LABELS_INTERNAL[sectionKey]}" aan.\n` +
      `${instruction}\n\n` +
      `Huidige tekst van deze sectie:\n${sectionContent}\n\n` +
      `Stuur het resultaat als documentUpdates voor "${sectionKey}". Reageer NIET met een vraag, herschrijf direct.`;

    const chatLabel = REGEN_CHAT_LABELS[action] || REGEN_CHAT_LABELS.rewrite;
    addMessage('user', `${chatLabel}: ${SECTION_LABELS_INTERNAL[sectionKey]}`);
    return callStructured({ message: prompt, temperature: 0.45 });
  }, [callStructured, addMessage, state.document]);

  /**
   * Edit a specific section with a user instruction + full document context.
   * Does NOT show the raw prompt in the chat, only a short friendly message.
   */
  const editSection = useCallback((sectionKey, instruction) => {
    const doc = state.document;
    const sectionContent = doc.sections[sectionKey]?.content || '';

    // Build a context summary of all sections so the AI understands the full vacancy
    const allSections = SECTION_ORDER
      .filter(k => doc.sections[k]?.content?.trim())
      .map(k => `[${SECTION_LABELS_INTERNAL[k]}]\n${doc.sections[k].content}`)
      .join('\n\n');

    const prompt =
      `CONTEXT. Dit is de volledige vacaturetekst tot nu toe:\n` +
      `Functietitel: ${doc.title || '(onbekend)'}\n\n` +
      `${allSections}\n\n` +
      `---\n` +
      `OPDRACHT: Pas ALLEEN de sectie "${sectionKey}" aan.\n` +
      `Gebruikersinstructie: "${instruction}"\n` +
      `Huidige tekst van deze sectie:\n${sectionContent}\n\n` +
      `Herschrijf deze sectie op basis van de instructie. Behoud de stijl en toon van de rest van het document. ` +
      `Stuur het resultaat als documentUpdates voor "${sectionKey}". Reageer NIET met een vraag, herschrijf direct.`;

    // Show a clean user message in chat instead of the raw prompt
    addMessage('user', `✏️ ${SECTION_LABELS_INTERNAL[sectionKey]}: ${instruction}`);
    // Lower temperature for precise section edits (less hallucination)
    return callStructured({ message: prompt, temperature: 0.45 });
  }, [callStructured, addMessage, state.document]);

  return useMemo(
    () => ({ initSession, sendMessage, sendCardResponse, uploadFile, processVoiceTranscript, syncVoiceProgress, regenerateSection, editSection, resetFirstConceptToast, isLoading }),
    [initSession, sendMessage, sendCardResponse, uploadFile, processVoiceTranscript, syncVoiceProgress, regenerateSection, editSection, resetFirstConceptToast, isLoading]
  );
}
